import { useMutation, useSuspenseQuery } from "@apollo/client";
import { Button, PrimaryFullScreenSpinner, toast, useAppForm } from "@repo/ui";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/deposits_/new")({
  head: () => opsHead("New Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z.object({
  amount: z
    .custom<Decimal>(Decimal.isDecimal, {
      message: "Please enter an amount",
    })
    .describe("Amount"),
  date: z.date({ error: "Required" }),
  description: z.string().optional(),
  organisationId: z.string().nonempty({ message: "Required" }),
  toBusinessEntityId: z.string().nonempty({ message: "Required" }),
  toAssetId: z.string().nonempty({ message: "Required" }),
});

type FormData = z.infer<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewDepositForm {
        organisations {
          id
          name
          businessEntities {
            id
            name
            assets {
              id
              name
              currency
            }
          }
        }
      }
    `),
  );

  const [createDepositMutation, { loading }] = useMutation(
    graphql(`
      mutation AdminNewDepositForm_Create($input: CreateDepositInput!) {
        createDeposit(input: $input) {
          deposit {
            id
            paymentReference
          }
        }
      }
    `),
  );

  if (!data.organisations) {
    throw new Error("expected organisations");
  }

  const form = useAppForm({
    defaultValues: {
      amount: new Decimal(0),
      date: new Date(),
      description: "",
      organisationId: "",
      toBusinessEntityId: "",
      toAssetId: "",
    } as FormData,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const defaultErrorMessage =
        "There was an error submitting your form. Please try again later.";

      await createDepositMutation({
        variables: {
          input: {
            amount: value.amount,
            date: value.date,
            description: value.description ?? null,
            organisationId: value.organisationId,
            toAssetId: value.toAssetId,
          },
        },
        onCompleted: (result) => {
          const deposit = result.createDeposit?.deposit;
          if (!deposit?.id) {
            toast({
              kind: "error",
              autoHide: false,
              title: "Failed to create deposit",
              description: defaultErrorMessage,
            });
            throw new Error("failed to create deposit");
          }
          toast({
            kind: "success",
            autoHide: true,
            title: "Created deposit",
            description: `Created deposit ${deposit.paymentReference ?? deposit.id}`,
          });
          navigate({ to: `/deposits/${deposit.id}` });
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || defaultErrorMessage;
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to create deposit",
            description: errorMessage,
          });
          throw error;
        },
      });
    },
  });

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedBusinessEntityId = useStore(
    form.store,
    (state) => state.values.toBusinessEntityId,
  );
  const selectedAssetId = useStore(
    form.store,
    (state) => state.values.toAssetId,
  );

  const organisationOptions = data.organisations
    .map((organisation) => ({
      value: organisation.id,
      label: organisation.name ?? `id: ${organisation.id}`,
      organisation,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOrganisation = organisationOptions.find(
    (option) => option.value === selectedOrganisationId,
  )?.organisation;

  const businessEntityOptions =
    selectedOrganisation?.businessEntities
      ?.map((businessEntity) => ({
        value: businessEntity.id,
        label: businessEntity.name ?? businessEntity.id,
        businessEntity,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const selectedBusinessEntity = businessEntityOptions.find(
    (option) => option.value === selectedBusinessEntityId,
  )?.businessEntity;

  const assetOptions =
    selectedBusinessEntity?.assets
      ?.map((asset) => ({
        value: asset.id,
        label: asset.name ?? asset.id,
        asset,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const selectedAsset = assetOptions.find(
    (option) => option.value === selectedAssetId,
  )?.asset;

  return (
    <AdminPageContainer testId="admin-deposits-new">
      <AdminPageHeader title="New Deposit" subtitle="Create a new deposit" />
      <form.AppForm>
        <form
          className="flex max-w-lg flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppField
            name="organisationId"
            listeners={{
              onChange: () => {
                form.resetField("toBusinessEntityId");
                form.resetField("toAssetId");
              },
            }}
          >
            {(field) => (
              <field.SelectField label="Organisation" placeholder="Select...">
                {organisationOptions.map((organisation) => (
                  <field.SelectItem
                    key={organisation.value}
                    value={organisation.value}
                  >
                    {organisation.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField
            name="toBusinessEntityId"
            listeners={{
              onChange: () => {
                form.resetField("toAssetId");
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Destination business entity"
                disabled={!selectedOrganisationId}
                placeholder={
                  !selectedOrganisationId
                    ? "Select organisation first"
                    : "Select..."
                }
              >
                {businessEntityOptions.map((businessEntity) => (
                  <field.SelectItem
                    key={businessEntity.value}
                    value={businessEntity.value}
                  >
                    {businessEntity.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="toAssetId">
            {(field) => (
              <field.SelectField
                label="Destination asset"
                disabled={!selectedBusinessEntityId}
                placeholder={
                  !selectedBusinessEntityId
                    ? "Select destination business entity first"
                    : "Select..."
                }
              >
                {assetOptions.map((asset) => (
                  <field.SelectItem key={asset.value} value={asset.value}>
                    {asset.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="amount">
            {(field) => (
              <field.CurrencyAmountField
                currency={selectedAsset?.currency ?? "AUD"}
                label="Amount"
                placeholder="Amount"
              />
            )}
          </form.AppField>

          <form.AppField name="date">
            {(field) => <field.DatePickerField placeholder="Pick a date" />}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.TextareaField label="Description" placeholder="Optional" />
            )}
          </form.AppField>

          <div className="flex justify-end">
            <Button variant="primary" type="submit" disabled={loading}>
              Create deposit
            </Button>
          </div>
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
